import { type Observable, Subject } from 'rxjs';

export type Payload = {
  id: string;
} & {
  type: 'init_cashier';
  payload: {
    paymentUrl: string;
  };
};

export type ExternalMessage = ({ id: string } & {
  type: 'cashier_result';
}) &
  (
    | {
        payload: { result: 'success' | 'dismissed' };
      }
    | { payload: { result: 'error'; text: string } }
  );

/* 'error'*/
// GCash bridge interface
interface GCashBridge {
  postMessage: (message: Payload) => void;
  onMessage?: (event: ExternalMessage) => void;
}

// Extend window interface to include GCash bridge
declare global {
  interface Window {
    my?: GCashBridge;
  }
}

export class BridgeMessager {
  private pendingMessages = new Map<
    string,
    { resolve: (result: ExternalMessage) => void; reject: (error: any) => void }
  >();
  private inboundMessagesSubject = new Subject<ExternalMessage>();
  private bridgeCheckInterval?: ReturnType<typeof setInterval>;

  public readonly inboundMessagesStream: Observable<ExternalMessage> = this.inboundMessagesSubject.asObservable();

  constructor() {
    this.initializeBridge();
  }

  public checkIfWithinSuperApp(): boolean {
    return typeof window.my?.postMessage === 'function';
  }

  private initializeBridge(): void {
    // Set up bridge detection
    this.bridgeCheckInterval = setInterval(() => {
      if (window.my && typeof window.my.postMessage === 'function') {
        this.setupMessageHandling();
        clearInterval(this.bridgeCheckInterval);
      }
    }, 100);

    // Cleanup interval after 10 seconds if bridge is not found
    setTimeout(() => {
      if (this.bridgeCheckInterval) {
        clearInterval(this.bridgeCheckInterval);
      }
    }, 10000);
  }

  private setupMessageHandling(): void {
    if (!window.my) {
      return;
    }

    // Set up message listener
    window.my.onMessage = (event: ExternalMessage) => {
      this.handleIncomingMessage(event);
    };
  }

  private handleIncomingMessage(message: ExternalMessage): void {
    // Handle acknowledgment messages
    const pendingMessage = this.pendingMessages.get(message.id);
    if (pendingMessage) {
      this.pendingMessages.delete(message.id);
      pendingMessage.resolve(message);
    }
    return;
  }

  public async sendMessage(msg: Payload): Promise<ExternalMessage> {
    return new Promise((resolve, reject) => {
      // Bridge is ready, send message immediately
      this.sendMessageInternal(msg, resolve, reject);
    });
  }

  /**
   * Purely used for testing purposes.
   */
  public async __DO_NOT_USE_OR_YOU_WILL_BE_FIRED__handleIncomingMessage(message: ExternalMessage) {
    return this.handleIncomingMessage(message);
  }

  private sendMessageInternal(
    msg: Payload,
    resolve: (result: ExternalMessage) => void,
    reject: (error: any) => void
  ): void {
    // Store pending message for acknowledgment
    this.pendingMessages.set(msg.id, { resolve, reject });

    // Send message through bridge
    window.my?.postMessage(msg);
  }
}

// Singleton instance
let bridgeMessagerInstance: BridgeMessager | null = null;

export function getBridgeMessager(): BridgeMessager {
  if (!bridgeMessagerInstance) {
    bridgeMessagerInstance = new BridgeMessager();
  }
  return bridgeMessagerInstance;
}
